import { useState, useEffect } from "react";
import OnboardingShell from "./components/OnboardingShell";
import { getAdminProducts } from "./admin/productData";

const WORKFLOW_AI_PRODUCTS = [
  { value: "videosage", label: "VideoSage (₹50 / interview)" },
  { value: "screensage", label: "ScreenSage (₹40 / interview)" },
  { value: "codesage", label: "CodeSage (₹60 / interview)" },
];

const AI_BUDDY_PRODUCTS = [
  { value: "aibuddy_basic", label: "AI Buddy Basic (₹4500 / Month)" },
  { value: "aibuddy_premium", label: "AI Buddy Premium (₹6500 / Month)" },
];

const VISIONIX_AI_PRODUCTS = [
  { value: "visionix_ai", label: "Visionix AI (₹1 / Month)" },
];

const PEOPLE_CONNECT_AI_PRODUCTS = [
  { value: "people_connect_ai", label: "People Connect AI (Standard Plan)" },
];

const VRM_AI_ESTATE_PRODUCTS = [
  { value: "vrm_ai_estate", label: "VRM AI Estate (Site Visit Calls)" },
];

const EXIT_INTELLIGENCE_PRODUCTS = [
  { value: "exit_intelligence", label: "Exit Intelligence (Interview Calls)" },
];

const SUBSCRIPTIONS = [
  { value: "Monthly", label: "Monthly" },
  { value: "Annual", label: "Annual" },
];

function Step2Agreement({ onNext, onBack }) {
  const [adminProductsList, setAdminProductsList] = useState(() => getAdminProducts());

  useEffect(() => {
    const syncProducts = () => {
      setAdminProductsList(getAdminProducts());
    };
    window.addEventListener("storage", syncProducts);
    window.addEventListener("vrm_products_updated", syncProducts);
    return () => {
      window.removeEventListener("storage", syncProducts);
      window.removeEventListener("vrm_products_updated", syncProducts);
    };
  }, []);

  const activeCategoryList = adminProductsList.filter((p) => p.status === "Active" || !p.status);

  const [signature, setSignature] = useState(() => localStorage.getItem("signature") || "");
  const [category, setCategory] = useState(() => {
    const saved = localStorage.getItem("productCategory");
    if (saved) return saved;
    if (activeCategoryList.length > 0) {
      const firstP = activeCategoryList[0];
      return firstP.slug || firstP.name.toLowerCase().replace(/[^a-z0-9]+/g, "_");
    }
    return "workflow_ai";
  });

  const [selectedProducts, setSelectedProducts] = useState(() => {
    try { return JSON.parse(localStorage.getItem("selectedProducts") || "{}"); }
    catch { return {}; }
  });
  const [subscription, setSubscription] = useState(() => localStorage.getItem("subscription") || "");
  const [goLiveDate, setGoLiveDate] = useState(() => localStorage.getItem("goLiveDate") || "");

  const currentSelectedProduct = activeCategoryList.find((p) => {
    const valKey = p.slug || p.name.toLowerCase().replace(/[^a-z0-9]+/g, "_");
    return valKey === category || p.slug === category || p.name.toLowerCase().replace(/[^a-z0-9]+/g, "_") === category;
  }) || activeCategoryList[0];

  const getSubProducts = (prod) => {
    if (!prod) return VISIONIX_AI_PRODUCTS;
    const key = (prod.slug || prod.name).toLowerCase().replace(/[^a-z0-9]+/g, "_");
    if (key.includes("workflow")) return WORKFLOW_AI_PRODUCTS;
    if (key.includes("buddy")) return AI_BUDDY_PRODUCTS;
    if (key.includes("visionix")) return VISIONIX_AI_PRODUCTS;
    if (key.includes("people")) return PEOPLE_CONNECT_AI_PRODUCTS;
    if (key.includes("estate")) return VRM_AI_ESTATE_PRODUCTS;
    if (key.includes("exit")) return EXIT_INTELLIGENCE_PRODUCTS;
    
    if (prod.plans && prod.plans.length > 0) {
      return prod.plans.map((pl, idx) => ({
        value: `${key}_plan_${idx}`,
        label: `${prod.name} ${pl.name} (${pl.price || prod.amount || "Standard"})`
      }));
    }

    return [
      { value: key, label: `${prod.name} (${prod.amount || prod.category || "Standard Plan"})` }
    ];
  };

  const activeProducts = getSubProducts(currentSelectedProduct);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = () => {
    if (!signature.trim()) {
      setError("Please type your full name as signature");
      return;
    }

    if (Object.keys(selectedProducts).length === 0 || !subscription) {
      setError("Please complete all required selections");
      return;
    }

    for (const key in selectedProducts) {
      if (!selectedProducts[key]) {
        setError(`Please enter expected number for ${key}`);
        return;
      }
    }

    if (!goLiveDate) {
      setError("Please select an expected go-live date");
      return;
    }

    setLoading(true);
    setError("");

    localStorage.setItem(
      "step2Data",
      JSON.stringify({ signature, category, selectedProducts, subscription, goLiveDate })
    );

    onNext();
  };

  return (
    <OnboardingShell
      step={2}
      title="Company agreement"
      subtitle="Review the agreement, choose your plan, and sign to continue."
      wide
    >
      <div className="field-group" style={{ marginTop: 22 }}>
        <label className="field-label" htmlFor="signature">Signature — type your full name</label>
        <input
          id="signature"
          className="onboard-input"
          placeholder="Full legal name"
          value={signature}
          onChange={(e) => {
            setSignature(e.target.value);
            localStorage.setItem("signature", e.target.value);
          }}
        />
      </div>

      <div className="field-group">
        <label className="field-label" htmlFor="product-category">Product Category</label>
        <select
          id="product-category"
          className="onboard-input"
          value={category}
          onChange={(e) => {
            setCategory(e.target.value);
            localStorage.setItem("productCategory", e.target.value);
            setSelectedProducts({});
            localStorage.setItem("selectedProducts", "{}");
          }}
        >
          {activeCategoryList.map((p) => {
            const valKey = (p.slug || p.name).toLowerCase().replace(/[^a-z0-9]+/g, "_");
            return (
              <option key={p.id || valKey} value={valKey}>
                {p.name}
              </option>
            );
          })}
        </select>
      </div>

      <div className="select-group">
        <div className="select-group-label">Product</div>
        <div className="option-grid">
          {activeProducts.map((p) => (
            <label key={p.value} className={`option-card ${selectedProducts[p.value] !== undefined ? "option-card--selected" : ""}`}>
              <input
                type="checkbox"
                name="product"
                value={p.value}
                checked={selectedProducts[p.value] !== undefined}
                onChange={(e) => {
                   const newSelections = { ...selectedProducts };
                   if (e.target.checked) {
                     newSelections[p.value] = ""; 
                   } else {
                     delete newSelections[p.value];
                   }
                   setSelectedProducts(newSelections);
                   localStorage.setItem("selectedProducts", JSON.stringify(newSelections));
                }}
              />
              {p.label}
            </label>
          ))}
        </div>
      </div>



      <div className="select-group">
        <div className="select-group-label">Subscription</div>
        <div className="option-grid">
          {SUBSCRIPTIONS.map((s) => (
            <label key={s.value} className={`option-card ${subscription === s.value ? "option-card--selected" : ""}`}>
              <input
                type="radio"
                name="subscription"
                value={s.value}
                checked={subscription === s.value}
                onChange={(e) => { setSubscription(e.target.value); localStorage.setItem("subscription", e.target.value); }}
              />
              {s.label}
            </label>
          ))}
        </div>
      </div>



      {Object.keys(selectedProducts).map(productKey => (
        <div key={productKey} className="field-group">
          <label className="field-label" htmlFor={`interviews-${productKey}`}>
            {category === "workflow_ai" ? "Expected number of interviews for " :
             category === "vrm_ai_estate" ? "Expected number of site visit calls for " :
             category === "exit_intelligence" ? "Expected number of interview calls for " :
             "Expected number of months / units for "}
            {activeProducts.find(p => p.value === productKey)?.label.split(' (')[0] || productKey}
          </label>
          <input
            id={`interviews-${productKey}`}
            className="onboard-input"
            type="number"
            min="0"
            placeholder={category === "workflow_ai" || category === "vrm_ai_estate" || category === "exit_intelligence" ? "e.g. 100" : "e.g. 1"}
            value={selectedProducts[productKey]}
            onChange={(e) => { 
               const newSelections = { ...selectedProducts, [productKey]: e.target.value };
               setSelectedProducts(newSelections);
               localStorage.setItem("selectedProducts", JSON.stringify(newSelections));
            }}
          />
        </div>
      ))}

      <div className="field-group">
        <label className="field-label" htmlFor="goLiveDate">Expected go-live date</label>
        <input
          id="goLiveDate"
          className="onboard-input"
          type="date"
          value={goLiveDate}
          onChange={(e) => { setGoLiveDate(e.target.value); localStorage.setItem("goLiveDate", e.target.value); }}
        />
      </div>

      {error && <p className="onboard-error">{error}</p>}

      <div className="button-row">
        <button className="onboard-button secondary" onClick={onBack} disabled={loading} type="button">
          Back
        </button>
        <button className="onboard-button onboard-button--primary" onClick={handleSubmit} disabled={loading} type="button">
          {loading ? "Submitting…" : "Accept & continue"}
        </button>
      </div>
    </OnboardingShell>
  );
}

export default Step2Agreement;